import React, { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'

import globalNotification from 'live/services/global-notification'
import i18n from 'live/services/i18n'

import { AbsoluteLoading } from 'plugin-codaf-risk-ext-bowtie/generic'
import { AlarpMethod } from 'plugin-codaf-risk-ext-bowtie/types/enums'

import { FieldRow, Fieldset, Input } from '../../../generic/Form'
import { listAlarp, updateAlarp } from '../../../services'
import { IAlarp } from '../../../types'
import { AlarpFieldRow, AlarpIndicator, AlarpThreshold } from '../styles'

interface IAlarpForm {
    acceptableRisk: string
    intolerableRisk: string
}

export const AlarpLevels: React.FC = () => {
    const [alarps, setAlarps] = useState<IAlarp[]>()
    const [editing, setEditing] = useState<AlarpMethod>()
    const [inLoading, setInLoading] = useState(false)
    const { handleSubmit, control, setValue, formState, watch } = useForm<IAlarpForm>({
        mode: 'all',
        defaultValues: { acceptableRisk: '', intolerableRisk: '' }
    })

    const onSave = async (data, id, methodType) => {
        const editedAlarp = alarps.filter(alarp => alarp.id === id)[0]

        const tresholdIntolerableRisk = Number(data[`intolerableRisk+${methodType}`])
        const tresholdAcceptableRisk = Number(data[`acceptableRisk+${methodType}`])

        editedAlarp.alarpThresholds.map(threshold => {
            if (threshold.alarpStatus.priorityOrder == 1) {
                threshold.threshold = tresholdIntolerableRisk
            }
            if (threshold.alarpStatus.priorityOrder == 0) {
                threshold.threshold = tresholdAcceptableRisk
            }
        })

        setInLoading(true)

        await updateAlarp(editedAlarp)
            .then(async () => {
                globalNotification.success(i18n('Alarp Thresholds updated successfully.'))
            })
            .catch(async onrejected => {
                const reject = await onrejected
                globalNotification.error(
                    reject?.Message ??
                        reject?.message ??
                        reject?.error ??
                        'Is not allow status Acceptable with value above to Intolerable.'
                )
            })
            .finally(async () => {
                setEditing(null)
            })

        await handleGetAlarps()

        setInLoading(false)
    }

    const onCancel = () => {
        setEditing(null)
        handleGetAlarps()
    }

    const handleGetAlarps = async () => {
        setInLoading(true)
        const data = await listAlarp()
        data.map(alarp => {
            const intolerableRiskThreshold = alarp.alarpThresholds.find(
                threshold => threshold.alarpStatus.priorityOrder === 1
            )
            const acceptableRiskThreshold = alarp.alarpThresholds.find(
                threshold => threshold.alarpStatus.priorityOrder === 0
            )
            setValue(`intolerableRisk+${alarp.methodTypeName}`, intolerableRiskThreshold.threshold.toExponential())
            setValue(`acceptableRisk+${alarp.methodTypeName}`, acceptableRiskThreshold.threshold.toExponential())
        })
        setInLoading(false)
        setAlarps(data)
    }

    const generateAlarpDefaultObject = (preset: '1' | '2') => {
        if (preset === '1') {
            setValue('acceptableRisk+QUALITATIVE', (9e-5).toExponential())
            setValue('intolerableRisk+QUALITATIVE', (9.9e-1).toExponential())
        }
        if (preset === '2') {
            setValue('acceptableRisk+QUALITATIVE', (9e-2).toExponential())
            setValue('intolerableRisk+QUALITATIVE', (9.9e-1).toExponential())
        }
    }

    useEffect(() => {
        handleGetAlarps()
    }, [])

    const AlarpLevel = ({ id, alarpThresholds, methodTypeName }: IAlarp) => {
        return (
            <Fieldset
                title={i18n(
                    'ALARP LEVELS FOR '?.toUpperCase() +
                        ((methodTypeName === 'QUALITATIVE' ? 'SEMI-QUALITATIVE' : methodTypeName) ?? '') +
                        ' BOWTIE'
                )}
                editing={editing === methodTypeName}
                onCancel={() => onCancel()}
                toggleEdit={() => setEditing(methodTypeName)}
                onSubmit={handleSubmit(data => {
                    onSave(data, id, methodTypeName)
                })}
                withFieldset={false}
            >
                <FieldRow>
                    <div style={{ padding: '16px' }}>
                        {i18n('Alarp Description') +
                            ' ' +
                            (methodTypeName === 'QUANTITATIVE'
                                ? ''
                                : i18n('For a semi-qualitative bowtie qualitative barriers are used.'))}
                    </div>
                </FieldRow>
                {editing === 'QUALITATIVE' && methodTypeName === 'QUALITATIVE' && (
                    <FieldRow>
                        <div className="semi-qualitative">
                            <div className="quali-button-text-bundle">
                                <div
                                    className={`quali-default-button def1 ${editing ? 'editing' : ''}`}
                                    onClick={() => generateAlarpDefaultObject('1')}
                                >
                                    {`${i18n('Semi-qualitative preset')} 1`}
                                </div>
                                <div className="quali-default-text def1">
                                    {`${i18n(
                                        'In this mode, you must have at least two active barriers in each threat plot to have a risk acceptance level (ALARP) as ACCEPTABLE. For the INTOLERABLE risk acceptance level to be obtained, all prevention barriers must be inactive. All mitigation barriers must also be inactive for the consequence to occur.'
                                    )} ${i18n(
                                        'These pre-settings work if you want to use active and inactive status, for the use of a third status, see the user guide'
                                    )}.`}
                                </div>
                            </div>
                            <div className="quali-button-text-bundle">
                                <div
                                    className={`quali-default-button def2 ${editing ? 'editing' : ''}`}
                                    onClick={() => generateAlarpDefaultObject('2')}
                                >
                                    {`${i18n('Semi-qualitative preset')} 2`}
                                </div>
                                <div className="quali-default-text def2">
                                    {`${i18n(
                                        'In this mode, you must have at least one active barrier in each threat layer to have a risk acceptance level (ALARP) as ACCEPTABLE. For the INTOLERABLE risk acceptance level to be obtained, all prevention barriers must be inactive. All mitigation barriers must also be inactive for the consequence to occur.'
                                    )} ${i18n(
                                        'These pre-settings work if you want to use active and inactive status, for the use of a third status, see the user guide'
                                    )}.`}
                                </div>
                            </div>
                        </div>
                    </FieldRow>
                )}

                <AlarpFieldRow>
                    <AlarpIndicator />
                    <AlarpThreshold top={'25%'} triangleDirection={'up'}>
                        <b>{i18n('Intolerable')}</b>
                        <b>{i18n('Highest acceptable level of risk')}</b>
                        <Input
                            control={control}
                            name={`intolerableRisk+${methodTypeName}`}
                            label={i18n('Risk Rate') + '(yr⁻¹):'}
                            groupStyle={{
                                border: 'none',
                                flex: 'none',
                                marginTop: '-30px',
                                marginRight: '60px'
                            }}
                            inputProps={{ readOnly: editing !== methodTypeName }}
                            rules={{ validate: v => (Number.isNaN(Number(v)) ? i18n('Value must be a number') : true) }}
                        />
                    </AlarpThreshold>
                    <AlarpThreshold top={'48%'} style={{ border: 'none' }}>
                        <b>{i18n('Tolerable')}</b>
                    </AlarpThreshold>
                    <AlarpThreshold top={'68%'} triangleDirection={'down'}>
                        <b>{i18n('Acceptable')}</b>
                        <b>{i18n('Generally acceptable level of risk')}</b>
                        <Input
                            control={control}
                            name={`acceptableRisk+${methodTypeName}`}
                            label={i18n('Risk Rate') + '(yr⁻¹):'}
                            groupStyle={{
                                border: 'none',
                                flex: 'none',
                                marginTop: '-30px',
                                marginRight: '60px'
                            }}
                            inputProps={{ readOnly: editing !== methodTypeName }}
                            rules={{ validate: v => (Number.isNaN(Number(v)) ? i18n('Value must be a number') : true) }}
                        />
                    </AlarpThreshold>
                </AlarpFieldRow>
            </Fieldset>
        )
    }

    if (alarps != null && alarps.length > 0) {
        return (
            <>
                {alarps.map(alarp => (
                    <AlarpLevel
                        key={alarp.id}
                        id={alarp.id}
                        alarpThresholds={alarp.alarpThresholds}
                        methodTypeName={alarp.methodTypeName}
                    />
                ))}
            </>
        )
    } else {
        return <AbsoluteLoading show={inLoading} />
    }
}
